import React, { useState, useEffect } from 'react';
import { get, post } from '../../api';
import Breadcrumbs from '../../components/Breadcrumbs';
import { Switch } from '@headlessui/react';
import { Tab } from '@headlessui/react';

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

const EmailMarketingPage = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [settings, setSettings] = useState(null);
    const [history, setHistory] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [settingsData, historyData] = await Promise.all([
                get('/api/1/admin/email-marketing/settings'),
                get('/api/1/admin/email-marketing/history')
            ]);
            setSettings(settingsData);
            setHistory(historyData);
            setError(null);
        } catch (err) {
            console.error("Failed to fetch email marketing data:", err);
            setError("Failed to load data. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleSave = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const data = Object.fromEntries(formData.entries());

        // Convert numbers
        data.batch_size = parseInt(data.batch_size);
        data.email_1_delay_hours = parseInt(data.email_1_delay_hours);
        data.email_2_delay_hours = parseInt(data.email_2_delay_hours);
        data.email_3_delay_hours = parseInt(data.email_3_delay_hours);
        data.email_4_delay_hours = parseInt(data.email_4_delay_hours);

        // Preserve enabled state
        data.enabled = settings.enabled;

        try {
            await post('/api/1/admin/email-marketing/settings', data);
            alert('Settings saved!');
            fetchData(); // Refetch to ensure sync
        } catch (err) {
            alert('Failed to save settings: ' + err.message);
        }
    };

    const toggleEnabled = async (checked) => {
        // Optimistic update
        const previousSettings = { ...settings };
        setSettings({ ...settings, enabled: checked });

        try {
            await post('/api/1/admin/email-marketing/settings', { enabled: checked });
            // Success, no need to do anything else as we optimistically updated
        } catch (err) {
            // Revert on failure
            setSettings(previousSettings);
            alert('Failed to update status: ' + err.message);
        }
    };

    const crumbs = [
        { label: 'Admin', path: '/app/admin' },
        { label: 'Email Marketing' }
    ];

    if (loading && !settings) return <div className="p-6">Loading...</div>;
    if (error) return <div className="p-6 text-red-500">{error}</div>;

    return (
        <div className="text-foreground p-6 max-w-6xl mx-auto">
            <Breadcrumbs crumbs={crumbs} />

            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-semibold">Email Marketing</h1>
                    <p className="text-muted-foreground">Configure automated marketing emails.</p>
                </div>
                <div className="flex items-center gap-3">
                    <span className="text-sm font-medium">{settings?.enabled ? 'Enabled' : 'Disabled'}</span>
                    <Switch
                        checked={settings?.enabled || false}
                        onChange={toggleEnabled}
                        className={classNames(
                            settings?.enabled ? 'bg-accent' : 'bg-gray-200',
                            'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2'
                        )}
                    >
                        <span
                            aria-hidden="true"
                            className={classNames(
                                settings?.enabled ? 'translate-x-5' : 'translate-x-0',
                                'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                            )}
                        />
                    </Switch>
                </div>
            </div>

            <Tab.Group selectedIndex={activeTab} onChange={setActiveTab}>
                <Tab.List className="flex space-x-1 rounded-xl bg-background-elevated p-1 mb-6">
                    {['Settings', 'History'].map((category) => (
                        <Tab
                            key={category}
                            className={({ selected }) =>
                                classNames(
                                    'w-full rounded-lg py-2.5 text-sm font-medium leading-5',
                                    'ring-white ring-opacity-60 ring-offset-2 ring-offset-accent focus:outline-none focus:ring-2',
                                    selected
                                        ? 'bg-white text-accent shadow'
                                        : 'text-muted-foreground hover:bg-white/[0.12] hover:text-white'
                                )
                            }
                        >
                            {category}
                        </Tab>
                    ))}
                </Tab.List>
                <Tab.Panels>
                    <Tab.Panel>
                        <form onSubmit={handleSave} className="space-y-8">
                            <div className="bg-background-elevated p-6 rounded-lg border border-border">
                                <h2 className="text-lg font-medium mb-4">General Settings</h2>
                                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                    <div>
                                        <label className="block text-sm font-medium text-muted-foreground">Batch Size</label>
                                        <input
                                            type="number"
                                            name="batch_size"
                                            defaultValue={settings?.batch_size}
                                            className="mt-1 block w-full rounded-md border-border bg-background px-3 py-2 text-foreground focus:border-accent focus:ring-accent sm:text-sm"
                                        />
                                        <p className="mt-1 text-xs text-muted-foreground">Number of emails to send per run (total across all types).</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-muted-foreground">Last Run</label>
                                        <div className="mt-1 px-3 py-2 text-foreground">
                                            {settings?.last_run_at ? new Date(settings.last_run_at).toLocaleString() : 'Never'}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {[1, 2, 3, 4].map((num) => (
                                <div key={num} className="bg-background-elevated p-6 rounded-lg border border-border">
                                    <h2 className="text-lg font-medium mb-4">Email {num}</h2>
                                    <div className="grid grid-cols-1 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-muted-foreground">Delay (Hours)</label>
                                            <input
                                                type="number"
                                                name={`email_${num}_delay_hours`}
                                                defaultValue={settings?.[`email_${num}_delay_hours`]}
                                                className="mt-1 block w-full rounded-md border-border bg-background px-3 py-2 text-foreground focus:border-accent focus:ring-accent sm:text-sm"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-muted-foreground">Subject</label>
                                            <input
                                                type="text"
                                                name={`email_${num}_subject`}
                                                defaultValue={settings?.[`email_${num}_subject`]}
                                                className="mt-1 block w-full rounded-md border-border bg-background px-3 py-2 text-foreground focus:border-accent focus:ring-accent sm:text-sm"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-muted-foreground">Preview Text</label>
                                            <input
                                                type="text"
                                                name={`email_${num}_preview_text`}
                                                defaultValue={settings?.[`email_${num}_preview_text`]}
                                                className="mt-1 block w-full rounded-md border-border bg-background px-3 py-2 text-foreground focus:border-accent focus:ring-accent sm:text-sm"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-muted-foreground">Body (HTML)</label>
                                            <textarea
                                                name={`email_${num}_body_html`}
                                                defaultValue={settings?.[`email_${num}_body_html`]}
                                                rows={10}
                                                className="mt-1 block w-full rounded-md border-border bg-background px-3 py-2 text-foreground focus:border-accent focus:ring-accent sm:text-sm font-mono"
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}

                            <div className="flex justify-end">
                                <button
                                    type="submit"
                                    className="bg-accent text-white px-4 py-2 rounded-md hover:bg-accent-hover transition-colors"
                                >
                                    Save Settings
                                </button>
                            </div>
                        </form>
                    </Tab.Panel>
                    <Tab.Panel>
                        <div className="bg-background-elevated rounded-lg border border-border overflow-hidden">
                            <table className="min-w-full divide-y divide-border">
                                <thead className="bg-muted">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">User</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Email #</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Sent At</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-background divide-y divide-border">
                                    {history?.logs?.map((log) => (
                                        <tr key={log.id}>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">{log.user_id}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">{log.email_number}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">{log.status}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                                                {new Date(log.sent_at).toLocaleString()}
                                            </td>
                                        </tr>
                                    ))}
                                    {(!history?.logs || history.logs.length === 0) && (
                                        <tr>
                                            <td colSpan="4" className="px-6 py-4 text-center text-sm text-muted-foreground">
                                                No history found.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </Tab.Panel>
                </Tab.Panels>
            </Tab.Group>
        </div>
    );
};

export default EmailMarketingPage;
